import { Link } from "@tanstack/react-router";
import { User, LayoutDashboard, Shield, Store, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/brand/logo";
import { LanguageSwitcher } from "@/components/language-switcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export function SiteHeader() {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo className="h-7 w-auto text-foreground" />

        <nav className="hidden items-center gap-8 md:flex">
          <Link to="/discover" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            {t("header.discover")}
          </Link>

          <a href="#featured" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            {t("header.featured")}
          </a>
          <a href="#how" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            {t("header.howItWorks")}
          </a>
          <a href="#pricing" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            {t("header.pricing")}
          </a>
          <a href="/partner" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            {t("header.forHosts")}
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link to="/login" className="hidden text-sm font-medium text-foreground/80 hover:text-foreground sm:block">
            {t("header.signIn")}
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center justify-center rounded-full bg-[#5958F3] px-4 py-2 text-sm font-medium text-white transition-all hover:opacity-90"
          >
            {t("header.signUp")}
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="relative grid h-10 w-10 place-items-center rounded-full bg-[#5958F3]/10 text-[#5958F3] transition-colors hover:bg-[#5958F3]/20 focus:outline-none"
                aria-label={t("header.profile")}
              >
                <User className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl border-border/60 p-1.5 shadow-soft">
              <div className="flex items-center gap-3 px-2 py-2">
                <Avatar className="h-9 w-9 bg-[#5958F3]/10 text-[#5958F3]">
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{t("header.guest")}</p>
                  <p className="truncate text-xs text-muted-foreground">guest@spayzo.app</p>
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className="cursor-pointer rounded-lg px-2 py-2">
                <Link to="/dashboard" className="flex items-center gap-2 text-sm">
                  <LayoutDashboard className="h-4 w-4" />
                  {t("header.dashboard")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer rounded-lg px-2 py-2">
                <Link to="/admin" className="flex items-center gap-2 text-sm">
                  <Shield className="h-4 w-4" />
                  {t("header.admin")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer rounded-lg px-2 py-2">
                <Link to="/merchant" className="flex items-center gap-2 text-sm">
                  <Store className="h-4 w-4" />
                  {t("header.merchant")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                asChild
                className="cursor-pointer rounded-lg px-2 py-2 text-destructive focus:text-destructive"
              >
                <Link to="/login" className="flex items-center gap-2 text-sm">
                  <LogOut className="h-4 w-4" />
                  {t("header.signOut")}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
